/**
 * API-слой для работы с Git: загрузка репозитория, файлов, веток, индексация.
 */
import apiReq from 'core/api';
import { globalSnackbar } from 'components/GlobalSnackbar';
import indexedDBStorage from 'utils/indexedDBStorage';
import { GIT_SELECTED_FILES_KEY } from '../constants';
import type {
  GitRequestData,
  GitFilesResponse,
  GitDownloadResponse,
  GitSaveResponse,
  GitIndexStatusResponse,
  FileTreeNode,
} from '../types';

export { GIT_SELECTED_FILES_KEY };

/** Загружает список файлов с сервера */
export async function fetchFiles(
  projectId: string,
  url: string,
  branch: string
): Promise<GitFilesResponse> {
  const data = await apiReq<GitFilesResponse>(
    `git/files?projectId=${projectId}&url=${encodeURIComponent(url)}&branch=${encodeURIComponent(branch)}`
  );
  return data ?? {};
}

/** Загружает содержимое файла */
export async function fetchFileContent(
  projectId: string,
  url: string,
  branch: string,
  fullPath: string
): Promise<string> {
  const params = new URLSearchParams({
    projectId,
    url,
    branch,
    path: fullPath,
  });
  const data = await apiReq<{ content?: string }>(`git/file?${params.toString()}`);
  return data?.content ?? '';
}

/** Запускает загрузку репозитория (возвращает taskId) */
export async function downloadRepository(
  requestData: GitRequestData
): Promise<GitDownloadResponse> {
  const data = await apiReq<GitDownloadResponse>('git/download', 'POST', requestData);
  return data ?? {};
}

/** Проверяет статус загрузки */
export async function getDownloadStatus(taskId: string): Promise<{ status?: string }> {
  const data = await apiReq<{ status?: string }>(`git/download/status?taskId=${taskId}`, 'GET');
  return data ?? {};
}

/** Загружает список веток */
export async function fetchBranches(
  requestData: GitRequestData
): Promise<Array<{ name: string; last_update?: string }>> {
  const data = await apiReq<Array<{ name: string; last_update?: string }>>(
    'git/branches',
    'POST',
    requestData
  );
  return data ?? [];
}

/** Сохраняет выбранные файлы в контекст (запуск индексации) */
export async function saveToContext(requestData: {
  url: string;
  files: Array<{ path: string; name: string }>;
  project_id: string;
}): Promise<GitSaveResponse> {
  const data = await apiReq<GitSaveResponse>('git/save', 'POST', requestData);
  return data ?? {};
}

/** Останавливает индексацию */
export async function stopIndexing(taskId: string | null): Promise<void> {
  await apiReq('git/index/stop', 'POST', { current_task_id: taskId });
}

export async function getIndexStatus(taskId: string): Promise<GitIndexStatusResponse> {
  const data = await apiReq<GitIndexStatusResponse>(`git/index/status?taskId=${taskId}`, 'GET');
  return data ?? {};
}

/** Возвращает количество проиндексированных файлов и общее число файлов */
export async function fetchGitContextCount(
  projectId: number | string
): Promise<{ count: number; total: number } | null> {
  const data = await apiReq<{ count: number; total: number }>(
    `git/context/count?projectId=${projectId}`,
    'GET'
  );
  return data ?? null;
}

/**
 * Загружает файлы с сервера и обновляет дерево.
 * Возвращает children дерева или пустой массив.
 */
export async function loadFilesFromServer(
  requestData: GitRequestData,
  gitKey: string
): Promise<FileTreeNode[]> {
  const filesData = await fetchFiles(requestData.project_id, requestData.url, requestData.branch);

  if (filesData.children && filesData.children.length > 0) {
    try {
      await indexedDBStorage.setItem(gitKey, { fileTree: filesData.children });
    } catch (err) {
      console.error('Ошибка сохранения выбранных файлов:', err);
    }
    globalSnackbar({ message: 'Файлы успешно загружены с сервера', severity: 'success' });
    return filesData.children;
  }

  return [];
}
